/**
 * DeleteDocumentButton — the reusable "Löschen" icon control for a single document,
 * gated `perms.can('document.delete') && document.state === 'inactive'` (DMS Part2
 * P2-13, dms/spec-part2-stefan-delta.md §1.3 — "identical to DocumentsScreen.tsx").
 *
 * Renders NOTHING (not disabled — hidden) when either gate fails: the permission
 * gate hides for non-admins (existing convention, DocumentsScreen §ds-7); the
 * inactive-only gate hides for an active document (an active policy's current PDF
 * can't be deleted — §1.2's "Zuerst auf inaktiv setzen" rule is expressed via the
 * ActionsMenu `disabled` variant on the ONE surface that needs it — PoliceDetailScreen's
 * header action — not here, since a row-level icon has no natural place for a tooltip
 * explanation).
 *
 * Fetches the document's live `state` via the EXISTING `GET /documents/:id`
 * (`useDocumentDetail`) rather than trusting a caller-supplied flag — a document can be
 * (re)activated independently via Ablage's own toggle (`document.set_state`), so the
 * policy surfaces must re-check state live, not infer it from the policy_version chain.
 *
 * [Source: apps/web/src/features/documents/DocumentsScreen.tsx] (icon-button + gate precedent)
 * [Source: apps/web/src/features/documents/hooks/useDeleteDocumentAction.ts] (confirm+delete+invalidate)
 */
import { Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { cn } from '../../../lib/cn';
import { useEffectivePermissions } from '../../administration/useEffectivePermissions';
import { useDocumentDetail } from '../hooks/useDocumentDetail';
import { useDeleteDocumentAction } from '../hooks/useDeleteDocumentAction';

export interface DeleteDocumentButtonProps {
  documentId: string;
  /** Used in the aria-label ("{label} löschen") + the confirm-dialog copy. */
  label: string;
  /** Called after a successful delete (e.g. a toast, or nothing — the caches already refetch). */
  onDeleted?: () => void;
  className?: string;
}

export function DeleteDocumentButton({
  documentId,
  label,
  onDeleted,
  className,
}: DeleteDocumentButtonProps) {
  const perms = useEffectivePermissions();
  const canDelete = perms.can('document.delete');
  // Zero-cost for non-admins: an empty id short-circuits useDocumentDetail's `enabled`.
  const { detail } = useDocumentDetail(canDelete ? documentId : '');
  const { deleteDocument } = useDeleteDocumentAction();

  if (!canDelete || detail?.state !== 'inactive') return null;

  async function handleClick() {
    if (await deleteDocument(documentId, label)) {
      onDeleted?.();
    }
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn('size-8 text-sem-alert hover:text-sem-alert', className)}
      aria-label={`${label} löschen`}
      onClick={() => void handleClick()}
    >
      <Trash2 className="size-3.5" />
    </Button>
  );
}
